import cyberpunk from "../../../apis/cyberpunk"
import history from "../../../history"
import { RESET_CACHES } from "../../user/redux/reducers"

// Cells duck — mirrors the pattern used in components/user/redux and
// components/post/redux. Action types are namespaced cyberpunk-media/.

const FETCH_CELLS = "cyberpunk-media/FETCH_CELLS"
const FETCH_CELL = "cyberpunk-media/FETCH_CELL"
const CREATE_CELL = "cyberpunk-media/CREATE_CELL"
const JOIN_CELL = "cyberpunk-media/JOIN_CELL"
const DELETE_CELL = "cyberpunk-media/DELETE_CELL"
const FETCH_CELL_POSTS = "cyberpunk-media/FETCH_CELL_POSTS"
const IS_LOADING = "cyberpunk-media/CELLS_IS_LOADING"
const ERROR = "cyberpunk-media/CELLS_ERROR"

const INITIAL_STATE = {
  list: [],
  single: null,
  posts: [],
  // True once the directory has been fetched — back-navigation reuses
  // the store; the page offers a manual Refresh instead.
  loaded: false,
  isLoading: false,
  isError: false,
  errorMessage: null
}

export const cellReducer = (state = INITIAL_STATE, action = {}) => {
  switch (action.type) {
    case FETCH_CELLS:
      return {
        ...state,
        list: action.payload,
        loaded: true,
        isLoading: false,
        isError: false
      }
    case FETCH_CELL:
      return { ...state, single: action.payload, isLoading: false, isError: false }
    case CREATE_CELL:
      return {
        ...state,
        list: [action.payload, ...(state.list || [])],
        single: action.payload,
        isLoading: false,
        isError: false
      }
    case JOIN_CELL:
      // Membership updates (join AND leave) — the payload is the bare
      // decorated cell, so merge into the list item to keep the
      // directory stats (txCount, tx7d, recentPosts) intact.
      return {
        ...state,
        single: action.payload,
        list: (state.list || []).map(c =>
          c._id === action.payload._id ? { ...c, ...action.payload } : c
        ),
        isLoading: false
      }
    case DELETE_CELL:
      return {
        ...state,
        list: (state.list || []).filter(c => c._id !== action.payload),
        single:
          state.single && state.single._id === action.payload ? null : state.single,
        isLoading: false
      }
    case FETCH_CELL_POSTS:
      return { ...state, posts: action.payload, isLoading: false }
    case IS_LOADING:
      return { ...state, isLoading: action.status }
    case RESET_CACHES:
      return INITIAL_STATE
    case ERROR:
      return { ...state, isError: true, errorMessage: action.payload, isLoading: false }
    default:
      return state
  }
}

const setLoading = status => ({ type: IS_LOADING, status })

const authHeader = () => {
  const token = JSON.parse(localStorage.getItem("jwt") || "null")
  const value = (token && token.token) || "noToken"
  cyberpunk.defaults.headers.common = { Authorization: `bearer ${value}` }
}

const to = promise => promise.then(data => [null, data]).catch(err => [err])

export const fetchCells = () => async dispatch => {
  dispatch(setLoading(true))
  authHeader()
  const [err, response] = await to(cyberpunk.get("/cells"))
  if (err) {
    dispatch({ type: ERROR, payload: err.message })
  } else {
    dispatch({ type: FETCH_CELLS, payload: (response.data && response.data.cells) || [] })
  }
}

export const fetchCell = cellId => async dispatch => {
  dispatch(setLoading(true))
  authHeader()
  const [err, response] = await to(cyberpunk.get(`/cell/${cellId}`))
  if (err) {
    dispatch({ type: ERROR, payload: err.message })
  } else {
    dispatch({ type: FETCH_CELL, payload: response.data })
  }
}

export const fetchCellPosts = cellId => async dispatch => {
  authHeader()
  const [err, response] = await to(cyberpunk.get(`/cell/${cellId}/posts`))
  if (err) {
    dispatch({ type: ERROR, payload: err.message })
  } else {
    dispatch({ type: FETCH_CELL_POSTS, payload: (response.data && response.data.posts) || [] })
  }
}

export const createCell = values => async dispatch => {
  dispatch(setLoading(true))
  authHeader()
  const [err, response] = await to(cyberpunk.post("/cells", values))
  if (err) {
    const message =
      (err.response && err.response.data && err.response.data.error) || err.message
    dispatch({ type: ERROR, payload: message })
  } else {
    dispatch({ type: CREATE_CELL, payload: response.data })
    history.push(`/cell/${response.data._id}`)
  }
}

// join/leave resolve to a success boolean so callers can sequence UI
// (pending spinners, confirmation flashes) on the real outcome.
export const joinCell = cellId => async dispatch => {
  authHeader()
  const [err, response] = await to(cyberpunk.post(`/cell/${cellId}/join`, {}))
  if (err) {
    dispatch({ type: ERROR, payload: err.message })
    return false
  }
  dispatch({ type: JOIN_CELL, payload: response.data })
  return true
}

export const leaveCell = cellId => async dispatch => {
  authHeader()
  const [err, response] = await to(cyberpunk.post(`/cell/${cellId}/leave`, {}))
  if (err) {
    dispatch({ type: ERROR, payload: err.message })
    return false
  }
  dispatch({ type: JOIN_CELL, payload: response.data })
  return true
}

export const deleteCell = cellId => async dispatch => {
  authHeader()
  const [err] = await to(cyberpunk.delete(`/cell/${cellId}`))
  if (err) {
    const message =
      (err.response && err.response.data && err.response.data.error) || err.message
    dispatch({ type: ERROR, payload: message })
  } else {
    dispatch({ type: DELETE_CELL, payload: cellId })
    history.push("/cells")
  }
}
